import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

const floors = [
  {
    id: 'wood',
    label: 'Behandlet Tre',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f1d8f1215_Tregulv.png',
    description: 'Varmebehandlet tregulv med oljebehandlet overflate for økt fuktmotstand. Behagelig å gå på og gir en naturlig badstuopplevelse.',
    pros: ['Behagelig og varmt å gå på', 'Naturlig badstuopplevelse', 'Oljebehandlet for fuktmotstand', 'Inkludert i prisen'],
    maintenance: 'Middels, jevnlig oljing anbefales',
    feel: 'Varm og organisk',
  },
  {
    id: 'stone',
    label: 'Steinflis',
    price: '+8 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/20a8ca98f_Steingulv.jpg',
    description: 'Naturlig steinflis som akkumulerer varme og avgir den jevnt. Lett å renholde, svært holdbart og gir et eksklusivt spa-preg.',
    pros: ['Akkumulerer og avgir varme', 'Svært lett å renholde', 'Eksklusivt spa-uttrykk', 'Ekstremt holdbart'],
    maintenance: 'Lavt, lett å rengjøre',
    feel: 'Kjølig og eksklusiv',
  },
  {
    id: 'heated',
    label: 'Varmeledende Gulv',
    price: '+15 000 kr',
    priceNote: 'Premium',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/8cd962d35_IKIJoylyJR_Lupekgreen.jpg',
    description: 'Varmeledende gulvsystem med flis og integrert varmeelement. Optimalt for helårsbruk, varmt gulv fra første steg inn i badstuen.',
    pros: ['Varmt gulv fra første steg', 'Optimalt for helårsbruk', 'Integrert varmeelement', 'Jevn temperatur hele gulvet'],
    maintenance: 'Lavt, selvregulererende',
    feel: 'Luksus og behagelig',
  },
];

export default function InnvendigGulv() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f1d8f1215_Tregulv.png"
          alt="Gulv" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 2</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Gulv</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Fra varmt tregulv til eksklusivt steingulv med varme — finn det rette underlaget
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke gulvmaterialer kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over gulvalternativene du kan velge mellom. Fra naturlig tregulv til helintegrert varmegulv.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {floors.map((floor, idx) => (
            <motion.div key={floor.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className="bg-white border border-border/20 rounded-[12px] overflow-hidden hover:shadow-xl hover:border-[#2d5a3d] transition-all">
              <div className="relative h-80 overflow-hidden">
                <img src={floor.img} alt={floor.label} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${floor.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : floor.priceNote === 'Premium' ? 'bg-[#2a1810] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {floor.price}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">{floor.label}</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-4 text-base">{floor.description}</p>
                <div className="bg-secondary/30 rounded-[6px] px-3 py-2 mb-4">
                  <p className="font-body text-xs text-foreground/50 uppercase tracking-widest">Vedlikehold</p>
                  <p className="font-body text-base text-foreground/70">{floor.maintenance}</p>
                </div>
                <div className="space-y-1">
                  {floor.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                      <span className="font-body text-base text-foreground/60">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {[
            { title: 'Budsjett', rec: 'Behandlet Tre', why: 'Inkludert, naturlig og behagelig å gå på.' },
            { title: 'Komfort + stil', rec: 'Steinflis', why: 'Akkumulerer varme og er ekstremt enkelt å holde rent.' },
            { title: 'Luksus helårs', rec: 'Varmeledende Gulv', why: 'Varmt gulv hele året — den ultimate komfortopplevelsen.' },
          ].map((item, idx) => (
            <div key={idx} className="border border-border/20 rounded-[12px] p-6 bg-white">
              <p className="font-body text-xs uppercase tracking-widest text-[#2d5a3d] mb-2">{item.title}</p>
              <p className="font-heading text-lg font-light text-[#2a1810] mb-2">{item.rec}</p>
              <p className="font-body text-base text-foreground/60">{item.why}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/innvendig-panel" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Forrige: Panel
          </a>
          <a href="/innvendig-dorer" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Neste: Dører <ArrowRight size={16} />
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}